import { Controller } from "@hotwired/stimulus"

// Python prelude injected before both student and solution code during validation.
// Replaces the real turtle module with a lightweight recording mock that prints
// each movement command as a line of text (captured via Sk.output).
// Cosmetic commands (pencolor, pensize, fillcolor, speed, shape) are ignored.
const RECORD_PRELUDE = `
_rt_log = []
class _RecordTurtle(object):
    def _log(self, line):
        print(line)
    def forward(self, d):
        self._log("fd:" + str(int(round(d + 0.0))))
    def fd(self, d):
        self.forward(d)
    def backward(self, d):
        self._log("bk:" + str(int(round(d + 0.0))))
    def bk(self, d):
        self.backward(d)
    def right(self, a):
        self._log("rt:" + str(int(round(a + 0.0))))
    def rt(self, a):
        self.right(a)
    def left(self, a):
        self._log("lt:" + str(int(round(a + 0.0))))
    def lt(self, a):
        self.left(a)
    def penup(self):
        self._log("pu")
    def pu(self):
        self._log("pu")
    def pendown(self):
        self._log("pd")
    def pd(self):
        self._log("pd")
    def begin_fill(self):
        self._log("bf")
    def end_fill(self):
        self._log("ef")
    def pencolor(self, c):
        pass
    def pensize(self, s):
        pass
    def fillcolor(self, c):
        pass
    def speed(self, s):
        pass
    def shape(self, s):
        pass
    def done(self):
        pass
    def write(self, *a):
        pass
    def goto(self, x, y=None):
        pass
    def setpos(self, x, y=None):
        pass
    def setheading(self, a):
        pass
    def home(self):
        pass
    def circle(self, r, e=None):
        pass
class _TurtleModule(object):
    def Turtle(self):
        return _RecordTurtle()
    def done(self):
        pass
    def Screen(self):
        return self
    def bgcolor(self, *a):
        pass
    def setup(self, *a):
        pass
    def exitonclick(self):
        pass
turtle = _TurtleModule()
`

export default class extends Controller {
  static targets = ["canvas", "errorOutput", "successBanner", "retryBanner", "hintBox", "hintBtn", "hintText", "hintRevealPrompt", "hint2Text", "hint2RevealPrompt"]
  static values = {
    exerciseId: Number,
    completeUrl: String
  }

  connect() {
    this._configSkulpt()
  }

  _configSkulpt() {
    const canvas = this.canvasTarget

    Sk.pre = "output"
    Sk.configure({
      output: () => {},
      read: (x) => {
        if (Sk.builtinFiles === undefined || Sk.builtinFiles["files"][x] === undefined) {
          throw "File not found: '" + x + "'"
        }
        return Sk.builtinFiles["files"][x]
      },
      __future__: Sk.python3,
      killableWhile: true,
      killableFor: true,
    })

    Sk.TurtleGraphics = {
      target: canvas,
      width: canvas.offsetWidth || 500,
      height: canvas.offsetHeight || 500,
    }
  }

  run() {
    this._clearCanvas()
    this._hideMessages()

    const code = this._getCode()
    if (!code) return

    Sk.TurtleGraphics = {
      target: this.canvasTarget,
      width: this.canvasTarget.offsetWidth || 500,
      height: this.canvasTarget.offsetHeight || 500,
    }

    Sk.misceval.asyncToPromise(() =>
      Sk.importMainWithBody("<stdin>", false, code, true)
    ).then(() => {
      this._validate(code)
    }).catch((err) => {
      this._showError(err.toString())
    })
  }

  // After visual run succeeds, compare student trace vs solution trace.
  _validate(studentCode) {
    const solutionEl = document.getElementById("solution-code")
    if (!solutionEl) {
      this._showSuccess(studentCode)
      return
    }

    const solutionCode = solutionEl.value

    Promise.all([
      this._record(studentCode),
      this._record(solutionCode)
    ]).then(([studentTrace, solutionTrace]) => {
      if (studentTrace.length > 0 && studentTrace === solutionTrace) {
        this._showSuccess(studentCode)
      } else {
        this._showRetry()
      }
    }).catch(() => {
      // Validation error — fall back to success so kids aren't blocked
      this._showSuccess(studentCode)
    })
  }

  // Run code through the recording mock, returns a normalized trace string.
  _record(code) {
    return new Promise((resolve) => {
      const lines = []

      const prevOutput = Sk.output
      Sk.configure({
        output: (text) => {
          const t = text.trim()
          if (t) lines.push(t)
        },
        read: (x) => {
          if (Sk.builtinFiles === undefined || Sk.builtinFiles["files"][x] === undefined) {
            throw "File not found: '" + x + "'"
          }
          return Sk.builtinFiles["files"][x]
        },
        __future__: Sk.python3,
        killableWhile: true,
        killableFor: true,
      })

      const strippedCode = this._stripImports(code)
      const fullCode = RECORD_PRELUDE + strippedCode

      Sk.misceval.asyncToPromise(() =>
        Sk.importMainWithBody("<recording>", false, fullCode, true)
      ).then(() => {
        Sk.configure({ output: prevOutput })
        resolve(lines.join("\n"))
      }).catch(() => {
        Sk.configure({ output: prevOutput })
        resolve("")
      })
    })
  }

  // Strip "import turtle" (and turtle.done) so the prelude's mock takes over.
  _stripImports(code) {
    return code
      .split("\n")
      .filter(line => {
        const t = line.trim()
        return !t.startsWith("import turtle") &&
               !t.startsWith("from turtle") &&
               t !== "turtle.done()"
      })
      .join("\n")
  }

  reset() {
    this._clearCanvas()
    this._hideMessages()

    const starterCode = document.getElementById("starter-code")?.value || ""
    const editorEl = document.querySelector('[data-controller~="editor"]')
    if (editorEl && editorEl._editorController) {
      editorEl._editorController.setValue(starterCode)
    }
  }

  showHint() {
    if (this.hasHintBoxTarget) {
      this.hintBoxTarget.classList.remove("hidden")
      if (this.hasHintBtnTarget) {
        this.hintBtnTarget.classList.add("hidden")
      }
    }
  }

  revealHint() {
    if (this.hasHintTextTarget) {
      this.hintTextTarget.classList.remove("blur-sm", "select-none", "cursor-pointer")
      this.hintTextTarget.removeAttribute("data-action")
    }
    if (this.hasHintRevealPromptTarget) {
      this.hintRevealPromptTarget.classList.add("hidden")
    }
  }

  revealHint2() {
    if (this.hasHint2TextTarget) {
      this.hint2TextTarget.classList.remove("blur-sm", "select-none", "cursor-pointer")
      this.hint2TextTarget.removeAttribute("data-action")
    }
    if (this.hasHint2RevealPromptTarget) {
      this.hint2RevealPromptTarget.classList.add("hidden")
    }
  }

  _clearCanvas() {
    const canvas = this.canvasTarget
    while (canvas.firstChild) {
      canvas.removeChild(canvas.firstChild)
    }
    this._configSkulpt()
  }

  _hideMessages() {
    if (this.hasErrorOutputTarget) {
      this.errorOutputTarget.classList.add("hidden")
      this.errorOutputTarget.textContent = ""
    }
    if (this.hasSuccessBannerTarget) {
      this.successBannerTarget.classList.add("hidden")
    }
    if (this.hasRetryBannerTarget) {
      this.retryBannerTarget.classList.add("hidden")
    }
  }

  _showSuccess(code) {
    if (this.hasSuccessBannerTarget) {
      this.successBannerTarget.classList.remove("hidden")
    }

    const url = this.completeUrlValue
    if (url) {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": token || "",
        },
        body: JSON.stringify({ code }),
      }).catch(() => {})
    }
  }

  _showRetry() {
    if (this.hasRetryBannerTarget) {
      this.retryBannerTarget.classList.remove("hidden")
    }
  }

  _showError(msg) {
    if (this.hasErrorOutputTarget) {
      let friendly = msg
      if (msg.includes("SyntaxError")) {
        friendly = "Erreur de syntaxe — vérifie ton code !\n\n" + msg
      } else if (msg.includes("NameError")) {
        friendly = "Erreur de nom — tu as bien écrit la commande ?\n\n" + msg
      } else if (msg.includes("IndentationError")) {
        friendly = "Erreur d'indentation — vérifie les espaces !\n\n" + msg
      }
      this.errorOutputTarget.textContent = friendly
      this.errorOutputTarget.classList.remove("hidden")
    }
  }

  _getCode() {
    const editorEl = document.querySelector('[data-controller~="editor"]')
    if (editorEl && editorEl._editorView) {
      return editorEl._editorView.state.doc.toString()
    }
    const textarea = document.querySelector("textarea[data-editor]")
    return textarea ? textarea.value : ""
  }
}
